"use client";

import React from "react";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { useConsent } from "@/components/consent-provider";
import SocialLinks from "@/components/social-links";
import { SUPPORT_EMAIL } from "@/lib/organization-json-ld";


// Directory badges required by free launch listings; removed once each launch has happened.
const DIRECTORY_BADGES = [
  {
    name: "LaunchIgniter",
    href: "https://launchigniter.com/product/briefpanel?ref=badge-briefpanel",
    src: "https://launchigniter.com/api/badge/briefpanel?theme=dark",
  },
  {
    name: "Nick Launches",
    href: "https://nicklaunches.com/products/briefpanel/",
    src: "https://nicklaunches.com/badges/featured-dark.png",
  },
  {
    name: "IndieHunt",
    href: "https://indiehunt.io/project/briefpanel",
    src: "https://indiehunt.io/badges/indiehunt-badge-dark.svg",
  },
  {
    name: "Findly.tools",
    href: "https://findly.tools/briefpanel",
    src: "https://findly.tools/badges/findly-tools-badge-dark.svg",
  },
  {
    name: "Twelve Tools",
    href: "https://twelve.tools",
    src: "https://twelve.tools/badge2-dark.svg",
  },
  {
    name: "SEO Directory",
    href: "https://seodirectory.io/",
    src: "https://seodirectory.io/badges/6a7d9d64-a146-4c9f-9536-46fd2b71176e/dark.svg",
  },
  {
    name: "OpenHunts",
    href: "https://openhunts.com",
    src: "https://cdn.openhunts.com/badges/club.webp",
  },
  {
    name: "Launch Llama",
    href: "https://tools.launchllama.co/products/briefpanel?utm_source=badge&utm_medium=referral",
    src: "https://tools.launchllama.co/featured-badge.png?v=2",
  },
  {
    name: "IndieTools",
    href: "https://www.indietools.app/products/briefpanel",
    src: "https://www.indietools.app/badges/listed-on-indietools-dark.png",
  },
  {
    name: "Fazier",
    href: "https://fazier.com/launches/briefpanel",
    src: "https://fazier.com/api/v1//public/badges/launch_badges.svg?badge_type=launched&theme=dark",
  },
  {
    name: "neeed",
    href: "https://neeed.directory/products/briefpanel?utm_source=briefpanel",
    src: "https://neeed.directory/badges/neeed-badge-dark.svg",
  },
  {
    name: "Acid Tools",
    href: "https://acidtools.com/ai/briefpanel",
    src: "https://acidtools.com/assets/images/badge-dark.png",
  },
  {
    name: "Good AI Tools",
    href: "https://goodaitools.com/ai/briefpanel",
    src: "https://goodaitools.com/assets/images/badge-dark.png",
  },
  {
    name: "FoundrList",
    href: "https://www.foundrlist.com/product/briefpanel?utm_source=badge&utm_medium=embed",
    src: "https://www.foundrlist.com/api/badge/briefpanel",
  },
  {
    name: "Startup Fast",
    href: "https://startupfa.st",
    src: "https://www.startupfa.st/images/badges/powered-by-dark.svg",
    // startupfa.st rejects badges smaller than 150×44.
    tall: true,
  },
  {
    name: "Huzzler",
    href: "https://huzzler.so/products/R8500GRLKI/briefpanel-com?utm_source=huzzler_product_website&utm_medium=badge&utm_campaign=free_listing",
    src: "https://huzzler.so/assets/images/embeddable-badges/featured.png",
  },
  {
    name: "ToolPilot",
    href: "https://www.toolpilot.ai",
    src: "https://www.toolpilot.ai/cdn/shop/files/f-b.png",
  },
  {
    name: "AI Tool Trek",
    href: "https://aitooltrek.com",
    src: "https://aitooltrek.com/images/logo.svg",
  },
  {
    name: "AIDirs",
    href: "https://aidirs.org",
    src: "https://aidirs.org/logo_new.png",
  },
  {
    name: "CircleSaaS",
    href: "https://circlesaasboard.com/startup/briefpanel-30b38a",
    src: "https://circlesaasboard.com/badge.svg",
  },
  {
    name: "Dang.ai",
    href: "https://dang.ai",
    src: "https://assets.dang.ai/badges/dang-verified-dark.png",
  },
  {
    name: "PostYourStartup",
    href: "https://postyourstartup.co/startup/briefpanel?ref=badge",
    src: "https://postyourstartup.co/api/badge/briefpanel?theme=dark",
  },
  {
    name: "LiftOff",
    href: "https://lift-off.sh/p/briefpanel",
    src: "https://lift-off.sh/images/badges/badgeLaunchedDark.webp",
  },
  {
    name: "SaaS Cubes",
    href: "https://saascubes.com",
    src: "https://saascubes.com/images/badges/badge-dark.png",
  },
  {
    name: "SiftFox",
    href: "https://siftfox.com/tool/briefpanel",
    src: "https://siftfox.com/badges/live-dark.svg",
  },
];

// Directories that ask for a plain text link instead of an image badge.
const TEXT_BADGES: { label: string; href: string; icon?: string }[] = [
  {
    label: "Featured on JustHunt",
    href: "https://justhunt.co/startups/briefpanel",
    icon: "https://justhunt.co/logo.png",
  },
  { label: "Listed on StartupGrower", href: "https://startupgrower.pages.dev" },
  { label: "AI Toolz Dir", href: "https://www.aitoolzdir.com" },
];

// Recolors any badge to the footer's blue so third-party colors don't clash.
const badgeTint =
  "[filter:grayscale(1)_sepia(1)_saturate(4)_hue-rotate(185deg)_brightness(0.85)]";

// `duplicate` is the marquee's second copy: hidden from screen readers and
// skipped by Tab, so every badge is announced and focused once.
const BadgeRow: React.FC<{ duplicate?: boolean }> = ({ duplicate }) => (
  <div
    aria-hidden={duplicate}
    className={`flex shrink-0 items-center gap-2 pr-2 ${duplicate ? "motion-reduce:hidden" : ""}`}
  >
    {DIRECTORY_BADGES.map((badge) => (
      <a
        key={badge.href}
        href={badge.href}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={duplicate ? -1 : undefined}
        className="shrink-0 opacity-80 transition-opacity hover:opacity-100"
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- third-party badges must load from each directory as-is */}
        <img
          src={badge.src}
          alt={`Featured on ${badge.name}`}
          className={`${badge.tall ? "h-11" : "h-8"} w-auto max-w-none ${badgeTint}`}
        />
      </a>
    ))}
    {TEXT_BADGES.map((badge) => (
      <a
        key={badge.href}
        href={badge.href}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={duplicate ? -1 : undefined}
        className={`inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-slate-600 px-2.5 text-[11px] font-semibold text-slate-200 opacity-80 transition-opacity hover:opacity-100 ${badgeTint}`}
      >
        {badge.icon && (
          // eslint-disable-next-line @next/next/no-img-element -- third-party logo must load from the directory as-is
          <img src={badge.icon} alt="" width={16} height={16} />
        )}
        {badge.label}
      </a>
    ))}
  </div>
);

const linkClass ="text-sm text-slate-400 transition-colors hover:text-white";
const headingClass =
  "text-xs font-semibold uppercase tracking-[0.08em] text-white";

const Footer: React.FC = () => {
  const t = useTranslations("footer");
  const locale = useLocale();
  const { openBanner } = useConsent();
  const currentYear = new Date().getFullYear();

  // Intentionally always dark; tuned to match the dark theme palette
  return (
    <footer className="bg-slate-900 pb-8 pt-12 text-slate-400 md:pt-16 dark:border-t dark:border-border">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto flex max-w-6xl flex-col gap-12">
          <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-[2fr_1fr_1fr_1fr] md:gap-12">
            <div className="col-span-2 flex max-w-xs flex-col gap-3 md:col-span-1">
              <p className="text-xl font-bold tracking-tight text-white">
                BriefPanel
              </p>
              <p className="text-sm leading-[22px]">{t("description")}</p>
              <SocialLinks />
            </div>

            <nav aria-label={t("product")} className="flex flex-col gap-4">
              <p className={headingClass}>{t("product")}</p>
              <div className="flex flex-col gap-3">
                <Link href="/radar" className={linkClass}>
                  {t("links.radar")}
                </Link>
                {/* The Black Friday radar exists in Portuguese only; a link on
                    every pt-BR page is what gets it crawled before the 27th. */}
                {locale === "pt-br" ? (
                  <Link href="/radar/black-friday" className={linkClass}>
                    {t("links.radarBlackFriday")}
                  </Link>
                ) : null}
                <Link href="/docs" className={linkClass}>
                  {t("links.docs")}
                </Link>
                <Link href="/blog" className={linkClass}>
                  {t("links.blog")}
                </Link>
              </div>
            </nav>

            <nav aria-label={t("legal")} className="flex flex-col gap-4">
              <p className={headingClass}>{t("legal")}</p>
              <div className="flex flex-col items-start gap-3">
                <Link href="/terms-of-service" className={linkClass}>
                  {t("links.terms")}
                </Link>
                <Link href="/privacy-policy" className={linkClass}>
                  {t("links.privacy")}
                </Link>
                <button type="button" onClick={openBanner} className={linkClass}>
                  {t("links.cookies")}
                </button>
              </div>
            </nav>

            <div className="flex flex-col gap-4">
              <p className={headingClass}>{t("company")}</p>
              <div className="flex flex-col gap-3">
                <Link href="/about" className={linkClass}>
                  {t("links.about")}
                </Link>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="break-all text-sm text-blue-300 transition-colors hover:text-white"
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-800 pt-6 md:flex-row md:items-center md:gap-5">
            <p className="shrink-0 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
              {t("featuredOn")}
            </p>
            {/* One row that scrolls on its own and fades at both edges. Users
                who prefer reduced motion get a still row they can scroll. */}
            <div className="min-w-0 flex-1 overflow-hidden [mask-image:linear-gradient(to_right,transparent,#000_3rem,#000_calc(100%-3rem),transparent)] motion-reduce:overflow-x-auto">
              <div className="flex w-max animate-marquee hover:[animation-play-state:paused] focus-within:[animation-play-state:paused] motion-reduce:animate-none">
                <BadgeRow />
                <BadgeRow duplicate />
              </div>
            </div>
          </div>

          {/* Shown as text, not behind "Contato": Google Ads grades the
              landing on whether the page says who is selling (#2909). */}
          <div className="flex flex-col justify-between gap-2 border-t border-slate-800 pt-6 text-[13px] md:flex-row md:gap-6">
            <p>{t("copyright", { year: currentYear })}</p>
            <p>{t("companyId")}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
